/* eslint-disable */
// import React, { useEffect } from "react";
import { BehaviorSubject, Observable, combineLatest } from "rxjs";
import {
  IComponentContext,
  UpdateCommand,
  ComponentContextReadyState,
  DataflowIntegration,
  ComponentContextCreateOptions,
  SingleValueParameter,
  ReportDefinition,
  MultiValueParameter,
  ParameterBase,
  MultiplicityType,
} from "@sa/rep-core";
import { dataService } from "../../dataFlow";
import { CubeDefinition, OLAPResult } from "@sa/rep-olap";
import { MDDEResult, SortingOptions } from "@sa/rep-mdde";
import { SelectionMode, AxisPosition, AggregationType, SortingDirection, DataType } from "@sa/rep-common";
import { ComponentContextCreateOptionsBasf, MDDEQuery, OLAPTransformer, ServicesRegister } from "../../shared";
import { debounceTime } from "rxjs/operators";
import { CUBE_ID, SLIDE_NUMBER } from "./constants";
import { clone, uniq } from "../../../../shared";
import { DictionariesService, DictionaryItem } from "@sa/rep-mdm";
import { string } from "prop-types";

export class ContextReport1 implements IComponentContext {
  public id: string;
  public readonly options: ComponentContextCreateOptionsBasf;
  public readonly dataflowIntegration: DataflowIntegration;
  public readyState: ComponentContextReadyState;
  protected _exportParameters = new Map<string, ParameterBase>();

  protected cube = new BehaviorSubject<CubeDefinition | null>(null);
  public payloadChart1 = new BehaviorSubject<OLAPResult | null>(null);
  public payloadChart2 = new BehaviorSubject<OLAPResult | null>(null);
  public payloadChart3 = new BehaviorSubject<OLAPResult | null>(null);

  public sources$ = new BehaviorSubject<string | null>(null);
  public commoditiesNames$ = new BehaviorSubject<Map<number, unknown>>(new Map());
  public countriesNames$ = new BehaviorSubject<Map<number, unknown>>(new Map());
  public commoditiesCountries$ = new BehaviorSubject<Map<number, number[]>>(new Map());
  public countriesRegionNames$ = new BehaviorSubject<Map<number, unknown>>(new Map());
  public countriesCodes$ = new BehaviorSubject<Map<number, unknown>>(new Map());

  protected _calendar: BehaviorSubject<string[]>;
  public get calendar(): Observable<string[]> {
    return this._calendar.asObservable();
  }

  protected _business: BehaviorSubject<number>;
  public get business(): Observable<number> {
    return this._business.asObservable();
  }

  constructor(id: string, createOptions: ComponentContextCreateOptionsBasf) {
    this.id = id;
    this.options = createOptions;

    this.dataflowIntegration = {
      canIntegrate: true, // Указываем, что компонент интегрируется к Dataflow сервису (и может управляться с помощью параметров отчёта)
      integrated: false,
    };

    this.readyState = ComponentContextReadyState.Loading;

    const calendarParameter = createOptions.dataflowService.getParameter("@calendar") as MultiValueParameter;
    this._calendar = new BehaviorSubject(calendarParameter.value as string[]);
    const businessParameter = createOptions.dataflowService.getParameter("@business-units") as SingleValueParameter;
    this._business = new BehaviorSubject(businessParameter.value as number);

    combineLatest([this._calendar, this._business])
      .pipe(debounceTime(500))
      .subscribe(([calendar, business]) => {
        this.query({ calendar, business });
      });

      const exportParameters = [
        "@e_countries", "@e_commodities"
      ];
      for (const eParamName of exportParameters) {
        let eParameter = this.options.dataflowService.getParameter(eParamName);
        if (eParameter === undefined) {
          this.options.dataflowService.parameters.add({
            id: eParamName,
            multiplicityType: MultiplicityType.Multiple,
            type: DataType.Number,
            allowNullable: false,
            value: []
          });
          eParameter = this.options.dataflowService.getParameter(eParamName);
        }
        this._exportParameters.set(eParamName, eParameter!);
      }
  }

  async tryIntegrate() {
    if (this.dataflowIntegration.canIntegrate && !this.dataflowIntegration.integrated) {
      this.dataflowIntegration.integrated = true; 
    }
  }

  async update(command: UpdateCommand): Promise<void> {
    const calendar = command.values.get("@calendar");
    if (calendar !== undefined) {
      this._calendar.next(calendar as string[]);
    }
    const business = command.values.get("@business-units");
    if (business !== undefined) {
      this._business.next(business as number);
    }
  }

  async query(parameters: { calendar: string[]; business: number }) {
    const { calendar, business } = parameters;
    this.readyState = ComponentContextReadyState.Loading;
    this.payloadChart3.next(null); 
    const dictionaryService = this.options.serviceProvider.resolve(ServicesRegister.Dictionary) as DictionariesService;
    const dictCommodities = await dictionaryService.getDictionary({
      id: 'slides-commodities-display',
      parameters: {
        'slide': SLIDE_NUMBER,
        'business_unit': business
      }
    });
    const itemsCommodities = (await dictCommodities.getAll()).sort((a, b) => {
      return a.getValue<number>('order')! - b.getValue<number>('order')!;
    });
    const commodities = itemsCommodities.map(x => x.getValue<number>('olap_commodity'));

    const { dataSourcesService } = this.options;
    const commodityId = Number(commodities[0]);
    const sources = await dataSourcesService.getDataSources(11, business, commodityId);
    this.sources$.next(sources);

    const dictMainCountries = await dictionaryService.getDictionary({
      id: 'slides-countries',
      parameters: {}
    });
    const itemsMainCountries = await dictMainCountries.getAll();
    const dictSlideCountries$ = commodities.map(commodity => (async () => {
      const dict = await dictionaryService.getDictionary({
        id: 'slides-countries-display',
        parameters: {
          'slide': 11,
          'olap_commodity': commodity,
          'business_unit': business
        }
      });
      const items = await dict.getAll();
      return [Number(commodity), items] as [number, DictionaryItem[]];
    })());
    const dictSlideCountries = (await Promise.all(dictSlideCountries$));
    const itemsCountries = dictSlideCountries.flatMap(([commodity, items]) => items).sort((a, b) => {
      return Number(a.id) - Number(b.id);
    });
    const itemsCountriesWithData = itemsCountries.filter(x => Boolean(x.getValue<number | null>('olap_country')));
    const countriesIds: Number[] = itemsCountriesWithData.map(x => x.getValue<number | null>('olap_country')) as number[];
    this.commoditiesCountries$.next(new Map(dictSlideCountries.map(([commodity, items]) => ([commodity, items.map(i => i.getValue<number>('olap_country')!)]))));
    this.commoditiesNames$.next(new Map(itemsCommodities.map(item => ([item.getValue<number>('olap_commodity')!, item.getValue('name')!]))));
    this.countriesNames$.next(new Map(itemsCountries.map(item => ([item.getValue<number>('olap_country')!, item.getValue('name')!]))));
    this.countriesRegionNames$.next(new Map(itemsCountriesWithData
      .filter(item => Boolean(item.getValue<number>('parent')))
      .map(item => ([
        item.getValue<number>('olap_country')!,
        itemsCountries.find(x => String(x.id) === String(item.getValue<number>('parent')))?.getValue('name')!
      ]))
    ));
    this.countriesCodes$.next(new Map(itemsMainCountries
      .map(item => ([
        item.getValue<number>('id')!,
        item.getValue<string>('code'),
      ]))
    ))



    const cubeSlideDef = await dataService.getCubeDefinition("slide_8_9_10_11");
    const cubeParameters = clone(cubeSlideDef.parameters);
    cubeParameters["p_commodities"]!["defaultValue"] = commodities.join(',');
    cubeParameters["p_countries"]!["defaultValue"] = countriesIds.join(',');
    cubeParameters["p_attributes"]!["defaultValue"] = [17].join(',');
    cubeParameters["p_calendar"]!["defaultValue"] = calendar.join(',');
    const cubeSlide = await dataService.getCube({
      id: "slide_8_9_10_11",
      parameters: cubeParameters,
    });


    const eCountriesParameter = this._exportParameters.get("@e_countries")! as MultiValueParameter; 
    eCountriesParameter.value = uniq(itemsCountriesWithData.map(country => Number(country.id)));
    const eCommoditiesParameter = this._exportParameters.get("@e_commodities")! as MultiValueParameter;
    eCommoditiesParameter.value = uniq(itemsCommodities.map(commodity => Number(commodity.id)));

    const olapResult3$ = cubeSlide.query({
      columns: {
        showEmpty: false,
        parts: [
          {
            path: "commodities.default",
            selection: commodities,
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      filters: {
        showEmpty: false,
        parts: [
          { path: "units.default", selection: [8], selectionMode: SelectionMode.Include },
          { path: "attributes.default", selection: [17], selectionMode: SelectionMode.Include },
          {
            path: "calendar.default",
            selection: calendar.map((x) => Number(x)),
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          {
            path: "countries.default",
            selection: countriesIds,
            selectionMode: SelectionMode.Include,
          },
          {
            path: "Measures", measures: [{ id: "change", aggregationType: AggregationType.Sum } ]
          },
        ],
      },
      sourceResolveOptions: {
        sourceId: cubeSlide.id,
      },
    } as any);
    let [olapResult3] = await Promise.all([olapResult3$]);
    
    this.readyState = ComponentContextReadyState.Loaded;
    this.payloadChart3.next(olapResult3);
  }
}
